import { useEffect, useState, useMemo } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchFriendsHistory } from '../services/statsService';
import { games } from '../data/games';
import { secondsToTimeString } from '../utils/scoreUtils';
import SectionBlock from './SectionBlock';

const COLORS = ['#f5a623', '#7ec8e3', '#a8e6cf', '#ff6b6b', '#c3b1e1', '#f5d76e'];

const TIME_PERIODS = [
  { label: 'Last week', days: 7 },
  { label: 'Last month', days: 30 },
  { label: 'Last 3 months', days: 90 },
  { label: 'Last 6 months', days: 180 },
  { label: 'Last year', days: 365 },
];

/** Convert a raw score string to a numeric value for plotting. */
const scoreToNumeric = (score, scoreType) => {
  if (score === 'X' || score == null) return null;

  if (scoreType === 'time') {
    // Mock data uses "M:SS", API stores integer seconds
    if (typeof score === 'string' && score.includes(':')) {
      const [min, sec] = score.split(':').map(Number);
      return min * 60 + sec;
    }
    return parseInt(score, 10);
  }

  return parseFloat(score);
};

/** Format a numeric value back to a human-readable score. */
const formatScore = (value, scoreType) => {
  if (value == null) return '';
  if (scoreType === 'time') return secondsToTimeString(value);
  return String(value);
};

/** Format an epoch timestamp to a short date label. */
const formatDate = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const selectClass =
  'w-full px-3 py-2 pr-8 bg-white/5 border border-nlg-rule text-nlg-text text-sm sm:text-base font-bold rounded-lg focus:outline-none focus:border-nlg-gold/50 transition-colors duration-150 cursor-pointer appearance-none';

const selectArrow = {
  backgroundImage:
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23f5d76e' stroke-width='3'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.35rem center',
  backgroundSize: '1rem 1rem',
};

/** Custom tooltip shown when hovering a data point. */
const ChartTooltip = ({ active, payload, scoreType }) => {
  if (!active || !payload?.length) return null;
  const { date, originalScore, message, username } = payload[0].payload;
  return (
    <div className="bg-nlg-block border border-nlg-rule rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-bold text-nlg-gold">{username}</p>
      <p className="text-nlg-muted">{formatDate(date)}</p>
      <p className="text-nlg-text font-semibold">
        Score: {scoreType === 'time' ? formatScore(scoreToNumeric(originalScore, scoreType), scoreType) : originalScore}
      </p>
      {message && <p className="text-nlg-muted italic mt-0.5">"{message}"</p>}
    </div>
  );
};

// "Stats" - a chart of the user's and friends' scores over time for a
// selected game. Lives on its own /stats route (see Stats.js), reached from
// the Scores section's footer; it came off Home in Phase 4.
function StatsSection() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [selectedPeriodDays, setSelectedPeriodDays] = useState(30);

  useEffect(() => {
    const loadData = async () => {
      try {
        const history = await fetchFriendsHistory();
        setHistoryData(history);

        // Default to the game with the most logged plays (ties broken alphabetically)
        const plays = history.user?.plays || [];
        const playCounts = plays.reduce((counts, p) => {
          counts[p.gameId] = (counts[p.gameId] || 0) + 1;
          return counts;
        }, {});
        const sorted = Object.keys(playCounts)
          .map((id) => Number(id))
          .map((id) => games.find((g) => g.id === id) || { id, name: `Game ${id}` })
          .sort((a, b) => (playCounts[b.id] - playCounts[a.id]) || a.name.localeCompare(b.name));
        if (sorted.length > 0) setSelectedGameId(sorted[0].id);
      } catch (err) {
        console.log('Error loading stats:', err);
        setError('Failed to load stats. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Games the user has played, sorted alphabetically
  const userGames = useMemo(() => {
    if (!historyData?.user?.plays) return [];
    const ids = [...new Set(historyData.user.plays.map((p) => p.gameId))];
    return ids
      .map((id) => {
        const g = games.find((game) => game.id === id);
        return g
          ? { id: g.id, name: g.name, scoreType: g.scoreType }
          : { id, name: `Game ${id}`, scoreType: null };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [historyData]);

  const selectedGame = useMemo(
    () => games.find((g) => g.id === selectedGameId) || null,
    [selectedGameId]
  );

  // Build per-user scatter datasets filtered by game + time period
  const { datasets, scoreType, domainMin, domainMax } = useMemo(() => {
    if (!historyData || selectedGameId == null) return { datasets: [], scoreType: null };

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - selectedPeriodDays);

    const sType = selectedGame?.scoreType || null;

    const allUsers = [
      { username: historyData.user.username, plays: historyData.user.plays, isUser: true },
      ...historyData.friends.map((f) => ({ username: f.username, plays: f.plays, isUser: false })),
    ];

    const sets = allUsers
      .map((user) => {
        const points = user.plays
          .filter((p) => p.gameId === selectedGameId && new Date(p.playDate) >= cutoff)
          .map((p) => ({
            date: new Date(p.playDate).getTime(),
            score: scoreToNumeric(p.score, sType),
            originalScore: p.score,
            message: p.message,
            username: user.username,
          }))
          .filter((p) => p.score !== null)
          .sort((a, b) => a.date - b.date);

        return { username: user.username, isUser: user.isUser, points };
      })
      .filter((d) => d.points.length > 0);

    const now = new Date();
    now.setHours(23, 59, 59, 999);
    return { datasets: sets, scoreType: sType, domainMin: cutoff.getTime(), domainMax: now.getTime() };
  }, [historyData, selectedGameId, selectedPeriodDays, selectedGame]);

  // Y-axis label
  const yLabel = useMemo(() => {
    if (!scoreType) return 'Score';
    if (scoreType === 'time') return 'Time';
    return 'Score';
  }, [scoreType]);

  return (
    <SectionBlock title="Stats">
      {isLoading && (
        <p className="px-2 py-4 text-sm text-nlg-muted text-center">Loading stats...</p>
      )}

      {!isLoading && error && (
        <div className="px-2 py-4 text-center">
          <p className="text-sm text-nlg-coral">{error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <div className="px-3 py-3 space-y-4">
          {userGames.length === 0 ? (
            <p className="text-sm text-nlg-muted text-center py-4">
              Log a score for one of your games to see stats here.
            </p>
          ) : (
            <>
              {/* Dropdowns */}
              <div className="flex gap-3">
                <div className="w-1/2">
                  <label htmlFor="game-picker" className="block text-sm font-semibold text-nlg-muted mb-1 text-center">
                    Game
                  </label>
                  <select
                    id="game-picker"
                    value={selectedGameId ?? ''}
                    onChange={(e) => setSelectedGameId(Number(e.target.value))}
                    className={selectClass}
                    style={selectArrow}
                  >
                    {userGames.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>

                <div className="w-1/2">
                  <label htmlFor="period-picker" className="block text-sm font-semibold text-nlg-muted mb-1 text-center">
                    Time Period
                  </label>
                  <select
                    id="period-picker"
                    value={selectedPeriodDays}
                    onChange={(e) => setSelectedPeriodDays(Number(e.target.value))}
                    className={selectClass}
                    style={selectArrow}
                  >
                    {TIME_PERIODS.map((p) => (
                      <option key={p.days} value={p.days}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Chart */}
              {datasets.length === 0 ? (
                <div className="text-center py-10 text-sm text-nlg-muted">
                  No data for this game in the selected time period.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="date"
                      type="number"
                      domain={[domainMin, domainMax]}
                      tickFormatter={formatDate}
                      name="Date"
                      tick={{ fontSize: 16, fill: '#8892b0' }}
                      stroke="#8892b0"
                    />
                    <YAxis
                      dataKey="score"
                      type="number"
                      name={yLabel}
                      tickFormatter={(v) => formatScore(v, scoreType)}
                      tick={{ fontSize: 16, fill: '#8892b0' }}
                      stroke="#8892b0"
                      label={{
                        value: yLabel,
                        angle: -90,
                        position: 'insideLeft',
                        style: { fontSize: 16, fill: '#8892b0' },
                      }}
                    />
                    <Tooltip
                      content={<ChartTooltip scoreType={scoreType} />}
                      cursor={{ strokeDasharray: '3 3' }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      wrapperStyle={{ paddingTop: 12, fontSize: 16, color: '#8892b0' }}
                    />
                    {datasets.map((ds, i) => (
                      <Scatter
                        key={ds.username}
                        name={ds.username}
                        data={ds.points}
                        fill={COLORS[i % COLORS.length]}
                        shape="circle"
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </div>
      )}
    </SectionBlock>
  );
}

export default StatsSection;
